import React from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { usePermissions } from '../hooks/usePermissions';

// Rotas liberadas quando o período de teste terminou e a empresa ainda não assinou.
const TRIAL_EXPIRED_ALLOWED_PATHS = ['/myaccount', '/plans', '/more'];

/**
 * Bloqueio de UI do teste grátis encerrado (flag `trial_expired` do /me).
 * - Dono da conta: qualquer rota fora de Minha Conta/Planos redireciona para /plans.
 * - Não dono: não acessa /plans nem /myaccount (telas só do dono); vê um aviso para pedir
 *   ao responsável que assine, com opção de sair (evita loop /plans <-> /home).
 * - sys_admin nunca é bloqueado.
 * O bloqueio some quando a empresa passa para um plano que não é de teste.
 */
export default function TrialGuard() {
  const { userData, trialExpired, loading } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();

  // Evita piscar conteúdo antes de saber se o teste terminou.
  if (loading) return null;
  if (!trialExpired || !userData || userData.sys_admin) return <Outlet />;

  if (userData.is_owner === true) {
    const allowed = TRIAL_EXPIRED_ALLOWED_PATHS.some(
      (path) => location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
    return allowed ? <Outlet /> : <Navigate to="/plans" replace state={{ trialExpired: true }} />;
  }

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_permissions_cache');
    navigate('/');
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, p: 3, textAlign: 'center' }}>
      <Typography component="h1" sx={{ fontSize: '19px', fontWeight: 800 }}>
        O período de teste da sua empresa terminou
      </Typography>
      <Typography sx={{ fontSize: '13px', color: 'text.secondary', maxWidth: 420 }}>
        Peça ao responsável pela conta para assinar um plano e liberar o acesso. Os dados da empresa foram mantidos.
      </Typography>
      <Button variant="outlined" size="small" onClick={handleLogout}>Sair</Button>
    </Box>
  );
}
